import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("asl_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // token invalid/expired
      // do not force redirect here; let pages handle gracefully
    }
    return Promise.reject(err);
  }
);

export const errMsg = (e, fallback = "Une erreur est survenue") =>
  e?.response?.data?.detail || e?.message || fallback;

// ---------------- Auth ----------------
export const authApi = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  testLogin: (email) => api.post("/auth/test-login", { email }),
  me: () => api.get("/auth/me"),
};

// ---------------- Members ----------------
export const membersApi = {
  directory: (sport_id) => api.get("/members/directory", { params: { sport_id } }),
  me: () => api.get("/members/me"),
  updateMe: (data) => api.put("/members/me", data),
  card: (id) => api.get(`/members/${id}`),
};

// ---------------- Sports ----------------
export const sportsApi = {
  list: () => api.get("/sports"),
  detail: (id) => api.get(`/sports/${id}`),
  create: (data) => api.post("/sports", data),
  update: (id, data) => api.put(`/sports/${id}`, data),
  remove: (id) => api.delete(`/sports/${id}`),
};

// ---------------- Events ----------------
export const eventsApi = {
  list: (params) => api.get("/events", { params }),
  create: (data) => api.post("/events", data),
  update: (id, data) => api.put(`/events/${id}`, data),
  remove: (id) => api.delete(`/events/${id}`),
  join: (id) => api.post(`/events/${id}/join`),
};

// ---------------- Finance ----------------
export const financeApi = {
  collectes: (active) => api.get("/collectes", { params: active !== undefined ? { active } : {} }),
  archive: () => api.get("/collectes/archive"),
  collecte: (id) => api.get(`/collectes/${id}`),
  createCollecte: (data) => api.post("/collectes", data),
  updateCollecte: (id, data) => api.put(`/collectes/${id}`, data),
  closeCollecte: (id) => api.post(`/collectes/${id}/close`),
  remind: (id) => api.post(`/collectes/${id}/remind`),
  checkout: (data) => api.post("/payments/checkout", data),
  status: (sid) => api.get(`/payments/status/${sid}`),
  interac: (data) => api.post("/payments/interac", data),
  myPayments: () => api.get("/payments/me"),
  allPayments: (pending_interac) => api.get("/payments", { params: { pending_interac } }),
  validateInterac: (id) => api.post(`/payments/${id}/validate-interac`),
};

// ---------------- Social ----------------
export const socialApi = {
  feed: (sport_id) => api.get("/social/feed", { params: { sport_id } }),
  createPost: (data) => api.post("/social/posts", data),
  like: (id) => api.post(`/social/posts/${id}/like`),
  comments: (id) => api.get(`/social/posts/${id}/comments`),
  addComment: (id, content) => api.post(`/social/posts/${id}/comments`, { content }),
  deletePost: (id) => api.delete(`/social/posts/${id}`),
};

// ---------------- Trophies ----------------
export const trophiesApi = {
  weeklyCurrent: () => api.get("/trophies/weekly/current"),
  award: (data) => api.post("/trophies/weekly", data),
  userShowcase: (id) => api.get(`/trophies/user/${id}`),
  leaderboard: (params) => api.get("/trophies/leaderboard", { params }),
  annual: (year) => api.get("/trophies/annual", { params: { year } }),
  computeMonthly: (data) => api.post("/trophies/compute-monthly", data),
  votes: () => api.get("/trophies/votes"),
  vote: (id, candidate_id) => api.post(`/trophies/votes/${id}/vote`, { candidate_id }),
  closeVote: (id) => api.post(`/trophies/votes/${id}/close`),
  monthlyCurrent: (month) => api.get("/trophies/monthly/current", { params: { month } }),
  hallOfFame: (params) => api.get("/trophies/hall-of-fame", { params }),
};

// ---------------- Notifications ----------------
export const notifApi = {
  list: (category) => api.get("/notifications", { params: { category } }),
  unreadCount: () => api.get("/notifications/unread-count"),
  read: (id) => api.post(`/notifications/${id}/read`),
  readAll: () => api.post("/notifications/read-all"),
};

// ---------------- Discipline ----------------
export const disciplineApi = {
  rules: (sport_id) => api.get("/discipline/rules", { params: { sport_id } }),
  createRule: (data) => api.post("/discipline/rules", data),
  updateRule: (id, data) => api.put(`/discipline/rules/${id}`, data),
  removeRule: (id) => api.delete(`/discipline/rules/${id}`),
  sanctions: () => api.get("/discipline/sanctions"),
  applySanction: (data) => api.post("/discipline/sanctions", data),
  liftSanction: (id) => api.post(`/discipline/sanctions/${id}/lift`),
};

// ---------------- Admin ----------------
export const adminApi = {
  stats: () => api.get("/admin/stats"),
  members: (params) => api.get("/admin/members", { params }),
  pending: () => api.get("/admin/pending-members"),
  approve: (id) => api.post(`/admin/members/${id}/approve`),
  reject: (id) => api.post(`/admin/members/${id}/reject`),
  setRole: (id, role) => api.put(`/admin/members/${id}/role`, { role }),
  coaches: () => api.get("/admin/coaches"),
};

// ---------------- Gallery ----------------
export const galleryApi = {
  albums: (sport_id, status) => api.get("/gallery/albums", { params: { sport_id, status } }),
  album: (id) => api.get(`/gallery/albums/${id}`),
  createAlbum: (data) => api.post("/gallery/albums", data),
  updateAlbum: (id, data) => api.put(`/gallery/albums/${id}`, data),
  deleteAlbum: (id) => api.delete(`/gallery/albums/${id}`),
  addPhotos: (id, images, caption) => api.post(`/gallery/albums/${id}/photos`, { images, caption }),
  updatePhoto: (id, data) => api.put(`/gallery/photos/${id}`, data),
  deletePhoto: (id) => api.delete(`/gallery/photos/${id}`),
};

export default api;
